import React, { useState, useEffect } from "react";
import { injectIntl, intlShape } from "react-intl";
import PropTypes from "prop-types";
import { TableCell, TableRow } from "@amzn/meridian/table";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Box from "@amzn/meridian/box";
import Alert from "@amzn/meridian/alert";
import StartTaskButton from "./StartTaskButton";
import {
  getExpiry,
  getTaskName,
  getTranslatedProcessName,
  getTranslatedTaskTypeName,
  priorityMapper,
  getEffectivePriority,
  shouldExpandRow,
  isElevatedSeverity
} from "../../../utils/Helper";

const renderExpander = (task) => {
  if (!shouldExpandRow(task)) return false;
  const { employeeLogin: coachee, manager } = task.additionalAttributes;
  if (!coachee) return false;
  return (
    <TableRow>
      <TableCell columnSpan={7}>
        <Box type="fill">
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "5rem",
              padding: 30,
            }}
          >
            <Row>
              <Text type="h100">Coachee&nbsp;&nbsp;&nbsp;:</Text>
              <Text>{coachee.attributeValue}</Text>
            </Row>
            {!!manager && (
              <Row>
                <Text type="h100">Manager&nbsp;&nbsp;&nbsp;:</Text>
                <Text>{manager.attributeValue}</Text>
              </Row>
            )}
            {task.id ? (
              <Row>
                <Text type="h100">Task Id :&nbsp;&nbsp;&nbsp;:</Text>
                <Text>{` ${task.id}`}</Text>
              </Row>
            ) : null}
          </div>
        </Box>
      </TableCell>
    </TableRow>
  );
};

const formattedEmployeeLogin = (employeeLogin) => {
  return employeeLogin.attributeValue
};

function TaskTableRow({ intl, task, rowComponents, isExpandAll }) {
  let { employeeLogin: associate, manager } = task.additionalAttributes;
  if (associate == null) {
    associate = task.additionalAttributes.REQUESTER;
  }
  const [open, setOpen] = useState(isExpandAll);
  useEffect(() => {
    setOpen(isExpandAll);
  }, [isExpandAll]);
  const effectivePriority = getEffectivePriority(task);
  const priority = priorityMapper(effectivePriority, intl);
  const formattedExpiry = getFormattedExpiry(task, intl);

  return (
    <TableRow
      open={open}
      onClick={() => setOpen(!open)}
      highlightOnHover
      key={task.id}
      rowComponents={rowComponents}
    >
      <TableCell>
        <Row spacing="200">
          <Text type={isElevatedSeverity(priority.value) ? "h100" : "b300"}>
            {priority.label}
          </Text>
          {isElevatedSeverity(priority.value) ? 
            <Alert size="small" type="error" /> : null}
        </Row>
      </TableCell>
      <TableCell>{getTaskName(task)}</TableCell>
      <TableCell>{getTranslatedProcessName(task.category, intl)}</TableCell>
      <TableCell>{getTranslatedTaskTypeName(task.type, intl)}</TableCell>
      <TableCell>{associate ? formattedEmployeeLogin(associate) : ""}</TableCell>
      <TableCell>{manager ? formattedEmployeeLogin(manager) : ""}</TableCell>
      <TableCell>{task.assignee}</TableCell>
      <TableCell>{formattedExpiry}</TableCell>
      <TableCell>
        <StartTaskButton task={task} />
      </TableCell>
      {renderExpander(task)}
    </TableRow>
  );
}

TaskTableRow.propTypes = {
  intl: intlShape.isRequired,
  task: PropTypes.shape.isRequired,
};

const getFormattedExpiry = (task, intl) => {
  const { dueDateTime } = task;
  if (!dueDateTime) return "";
  const totalHoursUntilExpiry = Number.parseFloat(getExpiry(dueDateTime)).toFixed(2);
  if (totalHoursUntilExpiry <= 0) return "OVERDUE";
  const minStr = intl.formatMessage({
    id: `overview.taskTable.time.min`,
    defaultMessage: "min",
  });
  const hrStr = intl.formatMessage({
    id: `overview.taskTable.time.hr`,
    defaultMessage: "hr",
  });
  const hrsStr = intl.formatMessage({
    id: `overview.taskTable.time.hrs`,
    defaultMessage: "hrs",
  });
  const dayStr = intl.formatMessage({
    id: `overview.taskTable.time.day`,
    defaultMessage: "day",
  });
  const daysStr = intl.formatMessage({
    id: `overview.taskTable.time.days`,
    defaultMessage: "days",
  });

  const numOfDaysUntilExpiry = Math.floor(totalHoursUntilExpiry / 24);
  const numOfHoursUntilExpiry = Math.floor(totalHoursUntilExpiry % 24).toFixed(0);
  const numOfMinutesUntilExpiry = Math.floor((totalHoursUntilExpiry * 60) % 60).toFixed(0);
  let expiryInReadableFormat = "";

  if (numOfDaysUntilExpiry >= 1) {
    expiryInReadableFormat = `${numOfDaysUntilExpiry} ${numOfDaysUntilExpiry > 1 ? daysStr : dayStr} `;
  }
  if (numOfHoursUntilExpiry >= 1) {
    expiryInReadableFormat += `${numOfHoursUntilExpiry} ${numOfHoursUntilExpiry > 1 ? hrsStr : hrStr} `;
  }

  if (numOfMinutesUntilExpiry >= 1) {
    expiryInReadableFormat += `${numOfMinutesUntilExpiry} ${minStr}`
  }

  return expiryInReadableFormat;
};

export default injectIntl(TaskTableRow);



// WEBPACK FOOTER //
// ./src/components/overview/TaskTable/TaskTableRow.jsx